export type SsoLoginTerminalType = "desktop" | "mobile" | "tablet";

export type BrowserSsoLoginClientContext = {
  user_agent?: string;
  browser_language?: string;
  screen_resolution?: string;
  terminal_type: SsoLoginTerminalType;
  os?: string;
  browser?: string;
};

function readOptionalBrowserString(value: string | null | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function readScreenResolution(screen: Screen): string | undefined {
  const width = Number(screen.width || 0);
  const height = Number(screen.height || 0);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return undefined;
  }
  return `${width}x${height}`;
}

function resolveTerminalType(userAgent: string | undefined): SsoLoginTerminalType {
  if (userAgent && /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)) {
    return /iPad|Tablet/i.test(userAgent) ? "tablet" : "mobile";
  }
  return "desktop";
}

function resolveOperatingSystem(userAgent: string | undefined): string | undefined {
  if (!userAgent) return undefined;
  if (/Windows NT/i.test(userAgent)) return "Windows";
  if (/Mac OS X/i.test(userAgent)) return /Mobile|iPhone|iPad/i.test(userAgent) ? "iOS" : "macOS";
  if (/Android/i.test(userAgent)) return "Android";
  if (/Linux/i.test(userAgent)) return "Linux";
  return undefined;
}

function resolveBrowser(userAgent: string | undefined): string | undefined {
  if (!userAgent) return undefined;
  if (/Edg\//i.test(userAgent)) return "Edge";
  if (/Chrome\//i.test(userAgent) && !/Chromium|Edg\//i.test(userAgent)) return "Chrome";
  if (/Safari\//i.test(userAgent) && !/Chrome\//i.test(userAgent)) return "Safari";
  if (/Firefox\//i.test(userAgent)) return "Firefox";
  return undefined;
}

export function buildBrowserSsoLoginClientContext(): BrowserSsoLoginClientContext {
  if (typeof window === "undefined") {
    throw new Error("SSO login client context requires a browser window");
  }

  const userAgent = readOptionalBrowserString(window.navigator.userAgent);
  const browserLanguage = readOptionalBrowserString(window.navigator.language);
  const screenResolution = readScreenResolution(window.screen);
  const os = resolveOperatingSystem(userAgent);
  const browser = resolveBrowser(userAgent);
  const clientContext: BrowserSsoLoginClientContext = {
    terminal_type: resolveTerminalType(userAgent),
  };

  if (userAgent) clientContext.user_agent = userAgent;
  if (browserLanguage) clientContext.browser_language = browserLanguage;
  if (screenResolution) clientContext.screen_resolution = screenResolution;
  if (os) clientContext.os = os;
  if (browser) clientContext.browser = browser;

  return clientContext;
}
